import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import parse from 'html-react-parser';

import { GIButton } from 'app-components';
import NavBarContainer from '../../../containers/common/NavBarContainer';
import Dropdown from '../../../lib/inputs/Dropdown';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { api } from '../../common-utils-services/axiosUtil';
import { configs } from '../../../constants/configs/configs';
import { environmentSelector } from '../../../selectors/commonSelectors';
import { useAppSelector } from '../../../hooks';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';

import { useIssuerList } from '../hooks/useIssuerList';
import { ALLOWED_FILE_TYPE_FOR_DRUGS, FILE_TYPE } from '../../../constants/common/fileTypes';
import { STATUS_CODE } from '../../../constants/common/apiResponse';
import { INTEGER_REGEX } from '../../../utils/validators/regex';
import { hasValidLoadDrugFile, loadDrugsSteps, showAdminPlanDrugModal } from '../utils';
import SideBar from '../components/SideBar';
import { IUploadDrugsFields, IUploadDrugsResponse } from '../types/uploadDrugs';
import {
    FlexRow,
    StyledDiv,
    StyledDropdown,
    StyledErrorMessage,
    StyledFileInput,
    StyledFileTypeContainer,
    StyledForm,
    StyledHeading,
    StyledLabel,
    StyledLayoutContainer,
} from '../styles';

const sidebarSteps = {
    heading: 'admin.loadDrugs.sideBar.heading',
    steps: loadDrugsSteps(),
};

const fileTypeRadioOptions = [
    {
        text: <FormattedMessage id="admin.loadDrugs.fileType.xml" />,
        value: FILE_TYPE.XML,
    },
    {
        text: <FormattedMessage id="admin.loadDrugs.fileType.json" />,
        value: FILE_TYPE.JSON,
    },
];

const LoadDrugs: React.FC = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const { stateCode } = useAppSelector(environmentSelector);
    const { issuerDropdownOptions } = useIssuerList(intl, 'admin.loadDrugs.form.issuer.placeholder');

    const currentYear = new Date().getFullYear();
    const yearList = [currentYear - 1, currentYear, currentYear + 1];

    const formSchema = z
        .object({
            issuer: z
                .string()
                .min(1, { message: intl.formatMessage({ id: 'admin.errors.issuerRequired' }) })
                .regex(INTEGER_REGEX, { message: intl.formatMessage({ id: 'admin.errors.carrierValidation' }) }),
            year: z.number().min(1, { message: intl.formatMessage({ id: 'admin.loadDrugs.errors.yearRequire' }) }),
            fileType: z.string(),
            file: z.any().refine(value => value && value.length > 0, {
                message: intl.formatMessage({ id: 'admin.loadDrugs.errors.selectDrugFile.required' }),
            }),
        })
        .superRefine((values, ctx) => {
            const { file, fileType } = values;

            if (file && file?.length && !hasValidLoadDrugFile(file[0], fileType)) {
                const errorMessages = {
                    [FILE_TYPE.XML]: 'admin.loadDrugs.errors.selectDrugFile.requiredXML',
                    [FILE_TYPE.JSON]: 'admin.loadDrugs.errors.selectDrugFile.requiredJSON',
                };

                const defaultError = 'admin.loadDrugs.errors.selectDrugFile.requiredFILE';
                const message: string = errorMessages[fileType] || defaultError;

                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: intl.formatMessage({ id: message }),
                    path: ['file'],
                });
            }
        });

    const {
        register,
        setValue,
        watch,
        reset,
        clearErrors,
        formState: { isDirty, isValid, errors },
    } = useForm<IUploadDrugsFields>({
        defaultValues: { issuer: '', year: currentYear, fileType: null, file: null },
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const values = watch();
    const fileType = watch('fileType');

    useEffect(() => {
        if (fileType) {
            setValue('file', null);
            clearErrors('file');
        }
    }, [fileType]);

    const handleFieldChange = (name: keyof IUploadDrugsFields, value: string | number) => {
        setValue(name, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const handleSubmit = async e => {
        e.preventDefault();
        const url = configs.endpoints.UPLOAD_DRUGS;
        const formData = new FormData();
        const issuerText = issuerDropdownOptions.find(issuer => issuer.value === values.issuer)?.text || '';
        const fields = {
            carrier: values.issuer,
            carrierText: issuerText,
            selectYear: values.year.toString(),
            fileType: values.fileType || '',
            fileToUpload: values.file?.[0] || '',
        };
        Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value);
        });
        
        try {
            const response = await api.post<IUploadDrugsResponse>(url, formData);
            if (response?.data) {
                if (response.data.status === STATUS_CODE.SUCCESS) {
                    showAdminPlanDrugModal(
                        dispatch,
                        STATUS_CODE.SUCCESS,
                        'admin.loadDrugs.uploadDrugs.success',
                        'admin.loadDrugs.heading',
                    );
                } else if (response.data.status === STATUS_CODE.FAIL) {
                    showAdminPlanDrugModal(
                        dispatch,
                        STATUS_CODE.FAIL,
                        response.data.errorMessage,
                        'admin.loadDrugs.heading',
                    );
                }
            }
            reset();
        } catch (error) {
            throw new Error(error);
        }
    };

    const handleCancel = () => {
        reset();
    };

    return (
        <StyledLayoutContainer id="loadDrugs" $stateCode={stateCode}>
            <div className="grid-container">
                <div className="grid-row">
                    <NavBarContainer navBarClass="ticket-mgmt-top-nav grid-col-12" />
                </div>
                <br />
                <h1>
                    <FormattedMessage id={'admin.loadDrugs.heading'} />
                </h1>

                <FlexRow>
                    <SideBar {...sidebarSteps} stateCode={stateCode} />

                    <StyledDiv $stateCode={stateCode}>
                        <StyledHeading $stateCode={stateCode} />
                        <StyledForm onSubmit={handleSubmit}>
                            <StyledDropdown $stateCode={stateCode}>
                                <StyledLabel htmlFor="issuer">
                                    {parse(intl.formatMessage({ id: 'admin.loadDrugs.form.label.issuer' }))}{' '}
                                    <span className="text-red">*</span>
                                </StyledLabel>
                                <Dropdown
                                    label={{ labelText: '' }}
                                    inputData={{
                                        id: 'issuer',
                                        options: issuerDropdownOptions,
                                        type: INPUT_TYPES.DROPDOWN,
                                        value: values.issuer,
                                        ...register('issuer'),
                                        inputClass: ['small-width'],
                                    }}
                                    inputActions={{
                                        handleFieldChange: val => {
                                            handleFieldChange('issuer', val);
                                        },
                                    }}
                                />
                                <ConditionalRendering isTrue={!!errors.issuer}>
                                    <StyledErrorMessage>{errors.issuer?.message} </StyledErrorMessage>
                                </ConditionalRendering>
                            </StyledDropdown>
                            <StyledDropdown $stateCode={stateCode}>
                                <StyledLabel htmlFor="year">
                                    {parse(intl.formatMessage({ id: 'admin.loadDrugs.form.label.year' }))}{' '}
                                    <span className="text-red">*</span>
                                </StyledLabel>
                                <Dropdown
                                    label={{ labelText: '' }}
                                    inputData={{
                                        id: 'year',
                                        options: yearList.map(year => ({ text: year, value: year })),
                                        type: INPUT_TYPES.DROPDOWN,
                                        value: values.year,
                                        ...register('year'),
                                        inputClass: ['small-width'],
                                    }}
                                    inputActions={{
                                        handleFieldChange: (val: number) => {
                                            handleFieldChange('year', Number(val));
                                        },
                                    }}
                                />
                                <ConditionalRendering isTrue={!!errors.year}>
                                    <StyledErrorMessage>{errors.year?.message}</StyledErrorMessage>
                                </ConditionalRendering>
                            </StyledDropdown>
                            <StyledFileTypeContainer>
                                <StyledLabel htmlFor="fileType">
                                    {parse(intl.formatMessage({ id: 'admin.loadDrugs.form.label.fileType' }))}{' '}
                                    <span className="text-red">*</span>
                                </StyledLabel>
                                <span>
                                    {fileTypeRadioOptions.map(({ text, value }, index) => (
                                        <span key={`${value}_${index}`}>
                                            <label>
                                                <input
                                                    type="radio"
                                                    {...register('fileType')}
                                                    value={value}
                                                    className="h-auto"
                                                />
                                                {'  '}
                                                {text}
                                            </label>
                                            {'  '}
                                        </span>
                                    ))}
                                </span>
                                <ConditionalRendering isTrue={!!errors.fileType}>
                                    <StyledErrorMessage>{errors.fileType?.message}</StyledErrorMessage>
                                </ConditionalRendering>
                            </StyledFileTypeContainer>
                            <div>
                                <StyledLabel htmlFor="file">
                                    {parse(intl.formatMessage({ id: 'admin.loadDrugs.form.label.drugFile' }))}{' '}
                                    <span className="text-red">*</span>
                                </StyledLabel>
                                <br />
                                <StyledFileInput
                                    id="file"
                                    type="file"
                                    disabled={!values.fileType}
                                    {...register('file')}
                                    accept={ALLOWED_FILE_TYPE_FOR_DRUGS}
                                />
                                <br />
                                <ConditionalRendering isTrue={!!errors.file}>
                                    <StyledErrorMessage>{errors.file?.message}</StyledErrorMessage>
                                </ConditionalRendering>
                            </div>

                            <FlexRow $isPadding>
                                <GIButton
                                    id="cancel"
                                    onClickHandler={handleCancel}
                                    variant="secondary"
                                    labelId="button.cancel"
                                    type="button"
                                    dataTestId="cancel-button"
                                />
                                <GIButton
                                    id="primary"
                                    type="submit"
                                    variant="primary"
                                    labelId="button.save"
                                    isDisabled={!(isDirty && isValid)}
                                    dataTestId="save-button"
                                />
                            </FlexRow>
                        </StyledForm>
                    </StyledDiv>
                </FlexRow>
            </div>
        </StyledLayoutContainer>
    );
};

export default LoadDrugs;
